import moment from "moment";
import mockInvoicesData from "../data/mockData/invoicesData";
import mockStatementsData from "../data/mockData/statementsData";
import { paginateRows } from "./listQueryUtils";
import { applySubUserSeedToRows } from "../data/mockData/subUserSeedAssignments";
import {
  getDemoInvoiceRows,
  getGeneratedOpenStatements,
  getStatementOverridesMap,
  markStatementsInvoiced,
  setGeneratedOpenStatements,
  upsertDemoInvoice,
  upsertStatementOverride,
} from "./demoInvoiceSessionStore";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function mergeById(baseRows, extraRows) {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extraRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), { ...(map.get(String(row.id)) || {}), ...row });
    }
  });
  return Array.from(map.values());
}

function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function statementAmountSeed(month, year) {
  return Math.round((((month * 31 + year * 7) % 450) + 50) * 100) / 100;
}

/** Match Add Accounting: only closed periods up to 4 months before the current month. */
function getLatestOpenStatementMonth() {
  return moment().subtract(4, "month").startOf("month");
}

function isValidStatementPeriod(month, year) {
  const period = moment({
    year: Number(year),
    month: Number(month) - 1,
  }).startOf("month");
  const latest = getLatestOpenStatementMonth();
  return period.isValid() && period.isSameOrBefore(latest, "month");
}

function isValidOpenStatement(row) {
  if (!row) {
    return false;
  }
  return (
    isValidStatementPeriod(row.month, row.year) &&
    isValidStatementPeriod(row.issue_month, row.issue_year)
  );
}

function applyStatementOverrides(rows) {
  const overrides = getStatementOverridesMap();
  return (rows || []).map(row => {
    const patch = overrides[String(row.id)];
    return patch ? { ...row, ...patch } : row;
  });
}

function ensureDemoOpenStatements() {
  const existing = getGeneratedOpenStatements();
  if (existing.length > 0 && existing.every(isValidOpenStatement)) {
    return existing;
  }

  const latest = getLatestOpenStatementMonth();
  const count = randomInt(4, 7);
  const statements = [];
  let cursor = latest.clone().subtract(count - 1, "month");
  const baseId = 9001;

  for (let i = 0; i < count; i += 1) {
    const month = cursor.month() + 1;
    const year = cursor.year();
    const amount = statementAmountSeed(month, year);
    const issueCursor = cursor.clone().add(1, "month");
    const issueMonth = issueCursor.isAfter(latest, "month")
      ? month
      : issueCursor.month() + 1;
    const issueYear = issueCursor.isAfter(latest, "month")
      ? year
      : issueCursor.year();

    statements.push({
      id: baseId + i,
      name: "Statement for John Doe",
      kind: 0,
      sts_kind: 2,
      quartal: 0,
      month,
      year,
      year_period: "",
      issue_month: issueMonth,
      issue_year: issueYear,
      short_description: `${MONTH_NAMES[month - 1]} ${year} Accounting Statement`,
      price: String(amount),
      currency: 0,
      status: 0,
      new_details: null,
      new_overview: null,
      invoice_generated: false,
      created: cursor.format("YYYY-MM-DD"),
    });

    cursor.add(1, "month");
  }

  setGeneratedOpenStatements(statements);
  return statements;
}

export function getStatementRows() {
  const generated = ensureDemoOpenStatements();
  const merged = mergeById(mockStatementsData, generated);
  return applyStatementOverrides(merged);
}

export function filterStatementsForInvoicing(rows, queryParams) {
  let filtered = [...(rows || [])];

  const status = queryParams?.get("status");
  if (status !== null && status !== "") {
    filtered = filtered.filter(row => String(row.status) === String(status));
  }

  const invoiceGenerated = queryParams?.get("invoice_generated");
  if (invoiceGenerated === "false") {
    filtered = filtered.filter(row => !row.invoice_generated);
    filtered = filtered.filter(isValidOpenStatement);
  } else if (invoiceGenerated === "true") {
    filtered = filtered.filter(row => !!row.invoice_generated);
  }

  return filtered;
}

export function resolveStatementsList(queryParams) {
  const rows = getStatementRows();
  const filtered = filterStatementsForInvoicing(rows, queryParams);
  return paginateRows(filtered, queryParams);
}

function enrichSubuserStatementRow(row) {
  const sid = Number(row?.id) || 0;
  return {
    visibility: sid % 3 === 0 ? "active" : "inactive",
    subuser_statement_emails_sent: sid % 5 === 0,
    subuser_report__parent_report: null,
    ...row,
  };
}

function filterSubuserStatements(rows, queryParams) {
  let filtered = (rows || []).map(enrichSubuserStatementRow);

  const parentReport = queryParams?.get("subuser_report__parent_report");
  if (parentReport != null && parentReport !== "") {
    const matched = filtered.filter(
      row => String(row.subuser_report__parent_report || "") === String(parentReport),
    );
    filtered = matched.length
      ? matched
      : filtered.slice(0, Math.min(filtered.length, 10)).map(row => ({
          ...row,
          subuser_report__parent_report: parentReport,
        }));
  }

  const visibility = queryParams?.get("visibility");
  if (visibility != null && visibility !== "") {
    filtered = filtered.filter(row => row.visibility === visibility);
  }

  return filterStatementsForInvoicing(filtered, queryParams);
}

export function resolveSubuserStatementsList(queryParams, subUserIdFromPath = null) {
  const rows = getStatementRows();
  const filtered = filterSubuserStatements(rows, queryParams);
  const seeded = applySubUserSeedToRows("subuser-statements", filtered);
  return paginateRows(seeded, queryParams, { subUserIdFromPath });
}

function findStatementRow(statementId) {
  const rows = getStatementRows();
  return rows.find(row => String(row.id) === String(statementId));
}

export function handleUpdateSubuserStatementStatus(payload) {
  const statementId = payload?.id;
  if (statementId == null || statementId === "") {
    throw new Error("Statement id is required.");
  }

  const row = findStatementRow(statementId);
  if (!row) {
    throw new Error("Statement not found.");
  }

  const nextStatus = Number(row.status) === 0 ? 1 : 0;
  upsertStatementOverride(statementId, { status: nextStatus });
  return { detail: "Statement status updated." };
}

export function handleUpdateSubuserStatementVisibility(payload) {
  const statementId = payload?.id;
  if (statementId == null || statementId === "") {
    throw new Error("Statement id is required.");
  }

  const row = enrichSubuserStatementRow(findStatementRow(statementId));
  if (!row?.id) {
    throw new Error("Statement not found.");
  }

  const nextVisibility = row.visibility === "active" ? "inactive" : "active";
  upsertStatementOverride(statementId, { visibility: nextVisibility });
  return { detail: "Statement visibility updated." };
}

export function handleBulkMarkSubuserStatementsVisible(payload) {
  const parentReport = payload?.parent_report;
  const rows = getStatementRows().map(enrichSubuserStatementRow);
  let targets = rows;

  if (parentReport != null && parentReport !== "") {
    const matched = rows.filter(
      row => String(row.subuser_report__parent_report || "") === String(parentReport),
    );
    targets = matched.length ? matched : rows;
  }

  targets.forEach(row => {
    upsertStatementOverride(row.id, { visibility: "active" });
  });

  return {
    detail: `${targets.length} statement(s) marked as visible.`,
  };
}

export function handleBulkSendSubuserStatementEmails(payload) {
  const parentReport = payload?.parent_report;
  const rows = getStatementRows().map(enrichSubuserStatementRow);
  let targets = rows.filter(row => !row.subuser_statement_emails_sent);

  if (parentReport != null && parentReport !== "") {
    const matched = targets.filter(
      row => String(row.subuser_report__parent_report || "") === String(parentReport),
    );
    targets = matched.length ? matched : targets;
  }

  targets.forEach(row => {
    upsertStatementOverride(row.id, { subuser_statement_emails_sent: true });
  });

  return {
    detail: "Emails queued for all relevant statements.",
  };
}

export function getOpenStatementsForInvoicing(queryParams) {
  const rows = getStatementRows();
  return filterStatementsForInvoicing(rows, queryParams);
}

export function getInvoiceRows() {
  return mergeById(mockInvoicesData, getDemoInvoiceRows());
}

export function getNextInvoiceId(rows) {
  return (rows || []).reduce((max, row) => {
    const val = Number(row?.id) || 0;
    return val > max ? val : max;
  }, 100) + 1;
}

export function buildInvoiceFromOpenStatements(openStatements, nextId) {
  const total = (openStatements || []).reduce(
    (sum, statement) => sum + (Number(statement.price) || 0),
    0,
  );
  const now = moment();

  return {
    id: nextId,
    invoice_number: `${now.format("DDMMYY")}${String(nextId).padStart(3, "0")}`,
    name: "Invoice for John Doe",
    type: "Standard Invoice",
    short_description: `Demo invoice for ${openStatements.length} open statement(s)`,
    amount: total.toFixed(2),
    vat_amount: "0.00",
    total_amount: total.toFixed(2),
    currency: 0,
    status: "Pending",
    issue_date: now.format("DD.MM.YYYY"),
    invoice_pdf: "",
  };
}

export function handleGenerateDemoInvoice() {
  const queryParams = new URLSearchParams({
    status: "0",
    invoice_generated: "false",
  });
  const openStatements = getOpenStatementsForInvoicing(queryParams);

  if (!openStatements.length) {
    throw new Error("No statement available for invoicing.");
  }

  const nextId = getNextInvoiceId(getInvoiceRows());
  const invoice = buildInvoiceFromOpenStatements(openStatements, nextId);

  upsertDemoInvoice(invoice);
  markStatementsInvoiced(openStatements.map(statement => statement.id));

  return invoice;
}
